/**
 * Permit application detail (prepare-to-attest).
 *
 * Shows the application fields, a tracked-status Select, a "Mark submitted (I
 * submitted it)" button, and the inspection list with the ability to record a
 * result a human/inspector reported.
 *
 * LEGAL / SAFETY: "Mark submitted" records that a HUMAN submitted the application
 * elsewhere — BEAM never submits. Inspection results record what an inspector
 * reported. NEVER imports @smartbox/* (mobile invariant 1). No raw style values.
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listPermitApplications,
  markPermitSubmitted,
  setPermitStatus,
  listInspections,
  recordInspectionResult,
  type PermitApplicationRow,
  type InspectionRow,
} from '../../../src/api/permits';
import {
  Screen,
  Text,
  Badge,
  Card,
  Button,
  Select,
} from '../../../src/components/ui';
import type { StatusKey } from '../../../src/theme';
import { theme } from '../../../src/theme';
import { extractErrorMessage, humanize, formatCurrency, formatDateTime } from '../../../src/lib/format';

const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'draft', label: 'Draft' },
  { value: 'prepared', label: 'Prepared' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'under_review', label: 'Under review' },
  { value: 'issued', label: 'Issued' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
  { value: 'closed', label: 'Closed' },
];

const INSPECTION_RESULT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'pass', label: 'Pass' },
  { value: 'fail', label: 'Fail' },
  { value: 'partial', label: 'Partial' },
  { value: 'cancelled', label: 'Cancelled' },
];

function permitBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'issued':
    case 'approved':
    case 'finalized':
    case 'closed':
      return { key: 'paid', label: humanize(status) };
    case 'submitted':
      return { key: 'sent', label: 'Submitted' };
    case 'prepared':
    case 'under_review':
    case 'in_review':
      return { key: 'inProgress', label: humanize(status) };
    case 'rejected':
    case 'denied':
    case 'expired':
      return { key: 'overdue', label: humanize(status) };
    default:
      return { key: 'draft', label: humanize(status) || 'Draft' };
  }
}

function inspectionBadge(result: string | null, status: string | null): { key: StatusKey; label: string } {
  const v = (result ?? status ?? '').toLowerCase();
  switch (v) {
    case 'pass':
    case 'passed':
      return { key: 'paid', label: 'Pass' };
    case 'fail':
    case 'failed':
      return { key: 'overdue', label: 'Fail' };
    case 'partial':
      return { key: 'inProgress', label: 'Partial' };
    case 'scheduled':
      return { key: 'sent', label: 'Scheduled' };
    default:
      return { key: 'draft', label: humanize(v) || 'Pending' };
  }
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.cardRow}>
      <Text variant="small" color="secondary">
        {label}
      </Text>
      <Text variant="data" style={styles.rowValue}>
        {value}
      </Text>
    </View>
  );
}

export default function PermitDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const queryClient = useQueryClient();

  const [status, setStatusValue] = useState<string | null>(null);
  const [resultByInspection, setResultByInspection] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);

  const appQuery = useQuery({
    queryKey: ['permit-applications'],
    queryFn: listPermitApplications,
    staleTime: 30_000,
  });
  const inspectionsQuery = useQuery({
    queryKey: ['permit-inspections', id],
    queryFn: () => listInspections(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const app: PermitApplicationRow | undefined = (appQuery.data ?? []).find((a) => a.id === id);

  const onMutError = (err: unknown) => setActionError(extractErrorMessage(err, 'Could not update the application.'));
  const refreshApps = () => {
    void queryClient.invalidateQueries({ queryKey: ['permit-applications'] });
    setActionError(null);
  };

  const submitMutation = useMutation({
    mutationFn: () => markPermitSubmitted(id!),
    onSuccess: refreshApps,
    onError: onMutError,
  });
  const statusMutation = useMutation({
    mutationFn: (s: string) => setPermitStatus(id!, s),
    onSuccess: () => {
      refreshApps();
      setStatusValue(null);
    },
    onError: onMutError,
  });
  const resultMutation = useMutation({
    mutationFn: ({ inspId, result }: { inspId: string; result: string }) =>
      recordInspectionResult(inspId, result),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['permit-inspections', id] });
      setActionError(null);
    },
    onError: onMutError,
  });

  if (!id) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Text variant="body" color="danger">
            No permit application ID provided.
          </Text>
        </View>
      </Screen>
    );
  }

  if (appQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel="Loading application" />
          <Text variant="body" color="secondary">
            Loading…
          </Text>
        </View>
      </Screen>
    );
  }

  if (appQuery.isError) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(appQuery.error, 'Failed to load application.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => appQuery.refetch()} />
          </Card>
        </View>
      </Screen>
    );
  }

  if (!app) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Text variant="body" color="secondary">
            This permit application could not be found.
          </Text>
        </View>
      </Screen>
    );
  }

  const b = permitBadge(app.status);
  const alreadySubmitted = !!app.submitted_at || app.status !== 'draft' && app.status !== 'prepared';
  const inspections = inspectionsQuery.data ?? [];

  return (
    <Screen scroll>
      <View style={styles.statusRow}>
        <Text variant="title">Permit application</Text>
        <Badge status={b.key} label={b.label} />
      </View>

      {/* Fields */}
      <Card>
        <Row label="Reference" value={app.permit_number || app.id.slice(0, 8).toUpperCase()} />
        <Row label="Status" value={humanize(app.status)} />
        {app.applicant_name ? <Row label="Applicant" value={app.applicant_name} /> : null}
        {app.project_address ? <Row label="Address" value={app.project_address} /> : null}
        {app.scope_of_work ? <Row label="Scope" value={app.scope_of_work} /> : null}
        {app.fee_amount ? <Row label="Fee" value={formatCurrency(app.fee_amount)} /> : null}
        {app.submitted_at ? <Row label="Submitted" value={formatDateTime(app.submitted_at)} /> : null}
      </Card>

      {/* Capability-tier filing surface (companion §1.2). Reachable by tapping (inv. 7). */}
      <View style={styles.section} testID="permit-detail">
        <Text variant="label" color="secondary">Filing</Text>
        <Card>
          <Button
            title="View submission packet"
            variant="secondary"
            size="sm"
            fullWidth={false}
            testID="permit-open-packet"
            onPress={() => router.push({ pathname: '/permits/packet', params: { appId: app.id } })}
            accessibilityLabel="Open the submission packet for this application"
          />
          <Button
            title="File this permit"
            variant="secondary"
            size="sm"
            fullWidth={false}
            testID="permit-open-file"
            onPress={() => router.push({ pathname: '/permits/file', params: { appId: app.id } })}
            accessibilityLabel="Open the tier-aware filing handoff for this application"
          />
          <Button
            title="Lifecycle, fees & corrections"
            variant="secondary"
            size="sm"
            fullWidth={false}
            testID="permit-open-lifecycle"
            onPress={() => router.push({ pathname: '/permits/lifecycle', params: { appId: app.id } })}
            accessibilityLabel="Open lifecycle, fees, corrections and eligibility for this application"
          />
        </Card>
      </View>

      {/* Mark submitted — records a HUMAN action */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Submission
        </Text>
        <Card>
          {app.submitted_at ? (
            <Text variant="body" color="secondary">
              Recorded as submitted on {formatDateTime(app.submitted_at)}.
            </Text>
          ) : (
            <>
              <Text variant="small" color="secondary">
                When you have submitted this application yourself in the jurisdiction’s
                portal, record it here so BEAM can track it. BEAM does not submit it for you.
              </Text>
              <Button
                title="Mark submitted (I submitted it)"
                variant="secondary"
                loading={submitMutation.isPending}
                disabled={submitMutation.isPending}
                onPress={() => submitMutation.mutate()}
                accessibilityLabel="Record that you submitted this application yourself"
              />
            </>
          )}
        </Card>
      </View>

      {/* Tracked status */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Update tracked status
        </Text>
        <Card>
          <Text variant="small" color="secondary">
            Keep BEAM in sync with what the jurisdiction has done. This only updates
            BEAM’s record — it does not change anything at the jurisdiction.
          </Text>
          <Select options={STATUS_OPTIONS} value={status ?? app.status} onChange={setStatusValue} />
          <Button
            title="Save status"
            variant="secondary"
            loading={statusMutation.isPending}
            disabled={statusMutation.isPending || !status || status === app.status}
            onPress={() => status && statusMutation.mutate(status)}
            accessibilityLabel="Save the tracked status"
          />
        </Card>
      </View>

      {/* Inspections */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Inspections
        </Text>
        {inspectionsQuery.isLoading ? (
          <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading inspections" />
        ) : inspectionsQuery.isError ? (
          <Card>
            <Text variant="body" color="danger">
              {extractErrorMessage(inspectionsQuery.error, 'Failed to load inspections.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => inspectionsQuery.refetch()} />
          </Card>
        ) : inspections.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">
              No inspections recorded yet.
            </Text>
          </Card>
        ) : (
          <View style={styles.list}>
            {inspections.map((insp: InspectionRow) => {
              const ib = inspectionBadge(insp.result, insp.status);
              const chosen = resultByInspection[insp.id] ?? null;
              return (
                <Card key={insp.id}>
                  <View style={styles.cardHeader}>
                    <Text variant="bodyStrong" style={styles.flex1}>
                      {insp.inspection_type ? humanize(insp.inspection_type) : 'Inspection'}
                    </Text>
                    <Badge status={ib.key} label={ib.label} />
                  </View>
                  {insp.scheduled_at ? (
                    <Text variant="small" color="secondary">
                      Scheduled {formatDateTime(insp.scheduled_at)}
                    </Text>
                  ) : null}
                  {insp.notes ? (
                    <Text variant="small" color="secondary">
                      {insp.notes}
                    </Text>
                  ) : null}
                  {insp.result ? (
                    <Text variant="small" color="secondary">
                      Recorded result: {humanize(insp.result)}
                    </Text>
                  ) : (
                    <View style={styles.inspActions}>
                      <Text variant="small" color="secondary">
                        Record the result the inspector reported:
                      </Text>
                      <Select
                        options={INSPECTION_RESULT_OPTIONS}
                        value={chosen}
                        onChange={(v) =>
                          setResultByInspection((prev) => ({ ...prev, [insp.id]: v }))
                        }
                      />
                      <Button
                        title="Record result"
                        variant="secondary"
                        size="sm"
                        loading={resultMutation.isPending}
                        disabled={resultMutation.isPending || !chosen}
                        onPress={() => chosen && resultMutation.mutate({ inspId: insp.id, result: chosen })}
                        accessibilityLabel="Record the inspection result the inspector reported"
                      />
                    </View>
                  )}
                </Card>
              );
            })}
          </View>
        )}
      </View>

      {actionError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {actionError}
          </Text>
        </Card>
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  section: { gap: theme.spacing.sm },
  list: { gap: theme.spacing.sm },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing.md,
  },
  rowValue: { flex: 1, textAlign: 'right' },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  flex1: { flex: 1 },
  inspActions: { gap: theme.spacing.sm },
});
